import { useMemo } from 'react';
import { useReduxState } from 'hooks';
import { filterValuesSeparatorSplitRegex } from 'shared-helpers/consts';
import { getMessageWithPlaceholders } from 'shared-locales';
import { Badge } from 'components/common';

import FiltersActiveDropdown from './FiltersActiveDropdown';
import SaveFiltersButton from './SaveFiltersButton';
import { useFiltersContext, useFiltersDispatch } from './FilterContext';

import type { FilterItem } from '.';

type Props =
  | { filterId: string; filterIds?: never }
  | { filterId?: never; filterIds?: string[] };

const FiltersActive = ({ filterId, filterIds }: Props) => {
  const { messages } = useReduxState();

  const { appliedParamsByFilterId, filters, savedFiltersAllowed } =
    useFiltersContext();

  const filterDispatch = useFiltersDispatch();

  const filtersByField = useMemo(() => {
    const filtersToUse = {} as Record<
      string,
      FilterItem & { referenceFilterId: string }
    >;
    const idsToParse = filterIds || [filterId];

    idsToParse.forEach((id) => {
      const filtersInFilterId = filters?.[id] || [];
      filtersInFilterId.forEach((filter) => {
        filtersToUse[filter.field] = { ...filter, referenceFilterId: id };
        if (filter.filters?.length) {
          filter.filters.forEach((subFilter) => {
            filtersToUse[subFilter.field] = {
              ...subFilter,
              referenceFilterId: id
            };
          });
        }
      });
    });

    return filtersToUse;
  }, [filters, filterId, filterIds]);

  const activeFilters = useMemo(() => {
    const keys = new Set<string>();
    const idsToParse = filterIds || [filterId];

    idsToParse.forEach((id) => {
      const applied = appliedParamsByFilterId[id] || {};
      Object.keys(applied).forEach((key) => {
        if (
          filtersByField[key]?.referenceFilterId === id &&
          applied[key]
        ) {
          keys.add(key);
        }
      });
    });

    return Array.from(keys);
  }, [appliedParamsByFilterId, filterId, filterIds, filtersByField]);

  const handleFilterRemove = (filterKey: string) => {
    const meta = filtersByField[filterKey];
    if (!meta) return;

    filterDispatch({
      type: 'removeAppliedFilterKey',
      field: filterKey,
      filterId: meta.referenceFilterId
    });
  };

  return activeFilters.length ? (
    <div className="filters-active">
      {activeFilters.map((activeFilter) => {
        let statusLabel = getMessageWithPlaceholders({
          message: messages.filtersWithStatus,
          placeholders: { itemTitle: '', statusName: '' }
        });
        let statusLabelMultiple = getMessageWithPlaceholders({
          message: messages.filtersWithStatusMultiple,
          placeholders: { itemTitle: '' }
        });
        if (activeFilter === 'competenceId') {
          statusLabel = getMessageWithPlaceholders({
            message: messages.filtersWithLevel,
            placeholders: { itemTitle: '', levelName: '' }
          });
          statusLabelMultiple = getMessageWithPlaceholders({
            message: messages.filtersWithLevelMultiple,
            placeholders: { itemTitle: '' }
          });
        }

        const filter = filtersByField[activeFilter];
        const refId = filter.referenceFilterId;

        const filterValues =
          appliedParamsByFilterId[refId]?.[activeFilter] || '';

        if (!filter.items?.length && !filter.input) {
          return (
            <Badge
              isRounded={false}
              onClose={() => handleFilterRemove(activeFilter)}
              text={`${filter.title}`}
            />
          );
        }

        let triggerClause = getMessageWithPlaceholders({
          message: messages.filtersIs,
          placeholders: { itemType: '', itemTitle: '' }
        });
        let triggerTitle = filterValues;
        let triggerSubClause = '';
        let triggerSubTitle = '';

        if (filter.items?.length) {
          const options = new Set<string>();
          const subLevels = new Set<string>();
          const values = filterValues
            .split(filterValuesSeparatorSplitRegex)
            .map((key) => {
              if (key === 'anyValueAllowed') {
                return {
                  label: messages.groupsUserAttributesAny,
                  subLabel: ''
                };
              }

              const [id, status] = key.split('.');
              const value = { label: '', subLabel: '' };

              const item = filter.items.find((item) => item.value === id);
              if (!item) return value;

              value.label = item.label;
              options.add(item.label);

              if (status) {
                const subItem = item.subItems.find(
                  (subItem) => subItem.value === status
                );
                if (subItem) {
                  value.subLabel = subItem.label;
                  subLevels.add(subItem.label);
                }
              }

              return value;
            });

          const presentInAllSource =
            appliedParamsByFilterId[refId]?.presentInAll;
          const presentInAll = (
            (presentInAllSource as string)?.split(
              filterValuesSeparatorSplitRegex
            ) || []
          ).includes(filter.field);

          if (options.size > 1) {
            triggerClause = getMessageWithPlaceholders({
              message: presentInAll
                ? messages.filtersIsInAll
                : messages.filtersIsOneOf,
              placeholders: { itemType: '', itemOptions: '' }
            });
          }

          triggerTitle = values[0].label;
          if (options.size > 1) {
            triggerTitle = presentInAll
              ? getMessageWithPlaceholders({
                  message: messages.filtersIsInAllOptions,
                  placeholders: {
                    itemMatchesAll: '',
                    numberOfOptions: options.size
                  }
                })
              : getMessageWithPlaceholders({
                  message: messages.filtersIsOneOfOptions,
                  placeholders: {
                    itemMatchesOneOf: '',
                    numberOfOptions: options.size
                  }
                });
          }

          triggerSubClause =
            subLevels.size > 1
              ? statusLabelMultiple
              : subLevels.size === 1
              ? statusLabel
              : '';

          triggerSubTitle =
            subLevels.size === 1 ? Array.from(subLevels)[0] : '';
        }

        return (
          <div className="filter" key={activeFilter}>
            <Badge
              isRounded={false}
              onClose={() => handleFilterRemove(activeFilter)}
              text={
                <FiltersActiveDropdown
                  filter={filter}
                  filterId={filter.referenceFilterId}
                  onClear={() => handleFilterRemove(activeFilter)}
                  triggerClause={triggerClause}
                  triggerSubClause={triggerSubClause}
                  triggerSubTitle={triggerSubTitle}
                  triggerTarget={filter.title}
                  triggerTitle={triggerTitle}
                />
              }
            />
          </div>
        );
      })}

      {savedFiltersAllowed && (
        <div className="save-filter">
          <SaveFiltersButton />
        </div>
      )}

      <style jsx>{`
        .filters-active {
          display: flex;
          flex-wrap: wrap;
          gap: 5px 10px;
          margin-top: 10px;
        }

        .save-filter {
          display: flex;
          align-items: center;
        }
      `}</style>
    </div>
  ) : null;
};

export default FiltersActive;
